/**
 * authConfig.js
 * OIDC configuration for WSO2 Identity Provider (which federates to Microsoft Entra ID).
 *
 * CURRENT STATUS:
 * :white_check_mark: Proxy working locally - discovery endpoint returns 200
 * :white_check_mark: Client ID recognized - WSO2 login page loads
 * :white_check_mark: In QA/Prod - calls WSO2 directly, no proxy needed
 * :white_check_mark: acr_values forces WSO2 to go direct to Entra
 * FIX: Override metadata endpoints locally so ALL oauth2 calls go through
 *      proxy — not just discovery. Without this, oidc-client-ts reads the
 *      token_endpoint from WSO2 discovery doc (real WSO2 URL) and POSTs
 *      directly to WSO2 → 403 CORS :x:
 *
 * FLOW - LOCAL:
 * localhost:3000
 *   → proxy intercepts GET /oauth2/token/.well-known/... → WSO2 (200) :white_check_mark:
 *   → signinRedirect() with acr_values → Microsoft Entra login
 *   → login with Microsoft credentials
 *   → redirect back to localhost:3000/logincallback
 *   → proxy intercepts POST /oauth2/token (token exchange) → WSO2 (200) :white_check_mark:
 *   → app loads logged in :white_check_mark:
 *
 * FLOW - QA/PROD:
 * unity-link-dev.shrss.com
 *   → calls WSO2 directly (no proxy, no CORS issue) :white_check_mark:
 *   → signinRedirect() with acr_values → Microsoft Entra login
 *   → login with Microsoft credentials
 *   → redirect back to unity-link-dev.shrss.com/logincallback :white_check_mark:
 *   → app loads logged in :white_check_mark:
 *
 * HOW metadata override works per environment:
 *   Local dev  → metadata block overrides ALL endpoints to localhost
 *                so proxy intercepts every oauth2 call (discovery, token, userinfo, jwks)
 *   QA/Prod    → metadata block is undefined
 *                oidc-client-ts uses real WSO2 URLs from discovery doc directly :white_check_mark:
 */

const rawAuthority = process.env.REACT_APP_OIDC_AUTHORITY || '';

/**
 * Builds the metadataUrl for LOCAL DEVELOPMENT ONLY.
 * Uses window.location.origin (actual port) so proxy intercepts correctly
 * even if CRA starts on port 3001/3002 instead of 3000.
 * Returns undefined in QA/Prod — not needed, WSO2 called directly.
 */
function buildDevMetadataUrl(authorityUrl) {
  if (process.env.NODE_ENV === 'production') return undefined;
  try {
    const parsed = new URL(authorityUrl);
    const pathname = parsed.pathname.replace(/\/$/, '');
    return `${window.location.origin}${pathname}/.well-known/openid-configuration`;
  } catch {
    return undefined;
  }
}

/**
 * Builds overridden metadata endpoints for LOCAL DEVELOPMENT ONLY.
 *
 * THE PROBLEM THIS SOLVES:
 * oidc-client-ts reads token_endpoint from WSO2 discovery doc.
 * WSO2 returns: "token_endpoint": "https://unity.qa.login.hardrock.com/oauth2/token"
 * So oidc-client-ts POSTs token exchange DIRECTLY to WSO2 → 403 CORS :x:
 *
 * THE FIX:
 * Override ALL endpoints to use window.location.origin (localhost).
 * Now every oauth2 call goes through setupProxy.js → forwarded to WSO2 :white_check_mark:
 *
 * issuer must still be the REAL WSO2 URL — used for token validation.
 * Changing issuer would break JWT signature verification :x:
 *
 * Returns undefined in QA/Prod — real WSO2 URLs used directly, no CORS issue.
 */
function buildDevMetadata(authorityUrl) {
  if (process.env.NODE_ENV === 'production') return undefined;
  try {
    const parsed = new URL(authorityUrl);
    const pathname = parsed.pathname.replace(/\/$/, ''); // e.g. /oauth2/token
    const origin = window.location.origin;               // e.g. http://localhost:3000

    // Real WSO2 base URL — used for endpoints the browser navigates to directly
    // (issuer validation, authorization redirect). Must NOT be the proxied localhost URL.
    // Comes from REACT_APP_OIDC_WSO2_BASE_URL in .env.local for local dev.
    // In QA/Prod this function is never called (returns undefined).
    const wso2Base = process.env.REACT_APP_OIDC_WSO2_BASE_URL || parsed.origin;

    return {
      // issuer MUST be real WSO2 URL — JWT iss claim is validated against this
      issuer: `${wso2Base}${pathname}`,

      // All other endpoints → localhost so proxy intercepts them
      // setupProxy.js forwards /oauth2/* to WSO2 server-side (no CORS)
      token_endpoint:         `${origin}${pathname}`,
      // authorization_endpoint → real WSO2 URL: browser navigates there directly for login
      authorization_endpoint: `${wso2Base}${pathname.replace('/token', '/authorize')}`,
      end_session_endpoint:   `${origin}/oidc/logout`,
      jwks_uri:               `${origin}${pathname.replace('/token', '')}/jwks`,
      userinfo_endpoint:      `${origin}${pathname.replace('/token', '')}/userinfo`,
      revocation_endpoint:    `${origin}${pathname.replace('/token', '')}/revoke`,
    };
  } catch {
    return undefined;
  }
}

const devMetadata = buildDevMetadata(rawAuthority);

const authConfig = {
  /**
   * WSO2 base URL — used for token/iss validation by oidc-client-ts.
   * Local:   http://localhost:3000/oauth2/token  (proxy intercepts)
   * QA:      https://unity.qa.login.hardrock.com/oauth2/token (direct)
   * Prod:    https://unity.login.hardrock.com/oauth2/token (direct)
   */
  authority: rawAuthority,

  /**
   * Explicit discovery URL.
   * Local:   http://localhost:{port}/oauth2/token/.well-known/openid-configuration
   *          → setupProxy.js forwards to WSO2 → HTTP 200 :white_check_mark:
   * QA/Prod: undefined → auto-discovers from authority :white_check_mark:
   */
  metadataUrl: buildDevMetadataUrl(rawAuthority),

  /**
   * LOCAL DEV ONLY: Override all endpoints to localhost.
   * Prevents oidc-client-ts from using real WSO2 URLs from discovery doc.
   * Real WSO2 URLs → direct browser calls → 403 CORS :x:
   * Localhost URLs → proxy intercepts → forwarded to WSO2 → 200 :white_check_mark:
   *
   * QA/PROD: undefined — oidc-client-ts uses real WSO2 URLs directly :white_check_mark:
   */
  ...(devMetadata && { metadata: devMetadata }),

  /** OAuth 2.0 client ID registered in WSO2 for UnityLink
   * Local:  IBf0G28tRyCRQQ3ipOFUKPHj4Asa  (from .env.local)
   * QA:     O7HHKd51vmGve5hjbLF78clRUUYa  (from .env.development)
   * Prod:   TBD                             (from .env.production)
   */
  client_id: process.env.REACT_APP_OIDC_CLIENT_ID || '',

    // Client secret — required by WSO2 for token exchange.
    // client_authentication: 'client_secret_post' sends client_id + client_secret
    // in the POST body ONLY — no Authorization: Basic header.
    // WSO2 rejects requests that have credentials in BOTH header and body (400 Bad Request).
    client_secret: process.env.REACT_APP_OIDC_CLIENT_SECRET || '',
    client_authentication: 'client_secret_post',
  /**
   * Must exactly match redirect URI registered in WSO2.
   * Local:  http://localhost:3000/logincallback
   * QA:     https://unity-link-dev.shrss.com/logincallback
   * Prod:   https://unity-link.shrss.com/logincallback
   */
  //  window.location.origin kicks in at runtime when variable not found in .env.
  redirect_uri: process.env.REACT_APP_OIDC_REDIRECT_URI ||
    `${window.location.origin}/logincallback`,

  /** openid required, profile for name, email for email address */
  scope: process.env.REACT_APP_OIDC_SCOPE || 'openid profile email',

  /** Where to send user after logout */
  post_logout_redirect_uri:
    process.env.REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI || window.location.origin,

  /**
   * PKCE authorization code flow.
   * Required by WSO2 for public clients (no client secret).
   * oidc-client-ts sends code_challenge automatically.
   */
  response_type: 'code',

  /** Keep session alive via silent iframe renewal */
  automaticSilentRenew: true,

  /** Store tokens in sessionStorage (cleared when tab closes) */
  userStore: undefined,

  /** Include id_token in logout request */
  revokeTokensOnSignout: true,

  /**
   * Force WSO2 to skip its own login page and go directly to Microsoft Entra.
   * WITHOUT this: WSO2 shows its own username/password page :x:
   * WITH this:    WSO2 immediately redirects to Microsoft Entra :white_check_mark:
   * If still seeing WSO2 page — ask Chathuranga for exact acr_values string.
   */
  extraQueryParams: {
    acr_values: 'urn:federation:MicrosoftOnline',
  },

  /**
   * Skip automatic /userinfo fetch after login.
   * false for now — userinfo endpoint may fail locally.
   * Change to true once full SSO flow works end-to-end.
   */
  loadUserInfo: false,
};

// ── Development warnings and logging ──────────────────────────────────────
if (process.env.NODE_ENV !== 'production') {
  if (!authConfig.authority) {
    console.warn(':warning: [Auth] REACT_APP_OIDC_AUTHORITY is not set — SSO will fail.');
  }
  if (!authConfig.client_id) {
    console.warn(':warning: [Auth] REACT_APP_OIDC_CLIENT_ID is not set — SSO will fail.');
  }
  // OIDC config debug log removed to avoid printing client_id / redirect_uri / authority to console.
}

export default authConfig;